import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, MousePointer, DollarSign, Users, Video, Heart } from "lucide-react";
import { useCampaignBreakdowns } from "@/hooks/useCampaignBreakdowns";
export default function CampaignDetailsModal({ campaign, dateRange, open, onOpenChange }) {
  if (!campaign) return null;

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatCurrency = (num) => {
    if (!num && num !== 0) return '$0';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Dados reais do Meta via Edge Function
  const { data: breakdowns } = useCampaignBreakdowns(campaign?.campaign_id, dateRange);
  const adSets = breakdowns?.adsets ?? [];

  const creatives = breakdowns?.creatives ?? [];


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{campaign.campaign_name}</DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className={
              campaign.status === 'active' ? 'bg-green-500/10 text-green-700 border-green-500/20' :
              campaign.status === 'paused' ? 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20' :
              'bg-gray-500/10 text-gray-700 border-gray-500/20'
            }>
              {campaign.status}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {campaign.market?.toUpperCase()}
            </span>
          </div>
        </DialogHeader>

        {/* Campaign Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                <span className="text-xs">Total Gasto</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(campaign.metrics?.spend)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs">Leads</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(campaign.metrics?.leads)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Eye className="h-4 w-4" />
                <span className="text-xs">Impressões</span>
              </div>
              <p className="text-2xl font-bold">{formatNumber(campaign.metrics?.impressions)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                <span className="text-xs">CPL</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(campaign.metrics?.cpl)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Ad Sets and Creatives */}
        <Tabs defaultValue="adsets" className="mt-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="adsets">Ad Sets ({adSets.length})</TabsTrigger>
            <TabsTrigger value="creatives">Creatives ({creatives.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="adsets" className="space-y-4 mt-4">
            {adSets.map((adSet) => (
              <Card key={adSet.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{adSet.name}</CardTitle>
                    {adSet.status && (
                      <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/20">
                        {adSet.status}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Impressões</div>
                      <div className="text-lg font-semibold">{formatNumber(adSet.impressions)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Clicks</div>
                      <div className="text-lg font-semibold">{formatNumber(adSet.clicks)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Gasto</div>
                      <div className="text-lg font-semibold">{formatCurrency(adSet.spend)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Leads</div>
                      <div className="text-lg font-semibold">{formatNumber(adSet.leads)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">CTR</span>
                      <span className="text-sm font-medium">{adSet.ctr.toFixed(2)}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">CPL</span>
                      <span className="text-sm font-medium">{formatCurrency(adSet.cpl)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="creatives" className="space-y-4 mt-4">
            {creatives.map((creative) => (
              <Card key={creative.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{creative.name}</CardTitle>
                      <Badge variant="outline" className="mt-1">
                        {creative.type === 'video' ? '🎥 Vídeo' : '🖼️ Imagem'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Impressões</div>
                      <div className="text-lg font-semibold">{formatNumber(creative.impressions)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Clicks</div>
                      <div className="text-lg font-semibold">{formatNumber(creative.clicks)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Gasto</div>
                      <div className="text-lg font-semibold">{formatCurrency(creative.spend)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Leads</div>
                      <div className="text-lg font-semibold">{formatNumber(creative.leads)}</div>
                    </div>
                  </div>

                  {creative.type === 'video' && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{formatNumber(creative.video_views)} views</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Heart className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{formatNumber(creative.engagement)} engajamentos</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">CTR</span>
                      <span className="text-sm font-medium">{creative.ctr.toFixed(2)}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">CPL</span>
                      <span className="text-sm font-medium">{formatCurrency(creative.cpl)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
